import { NotificationType } from '@today/types'
import { Button, Switch } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** The human-readable name of each notification type. */
export const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
	[NotificationType.WELCOME_EMAIL]: 'Welcome email',
	[NotificationType.INVITATION]: 'Invitation',
	[NotificationType.CONTENT_REVIEW_REQUESTED]: 'Content review requested',
	[NotificationType.CONTENT_APPROVED]: 'Content approved',
	[NotificationType.CONTENT_REJECTED]: 'Content rejected',
	[NotificationType.CONTENT_PUBLISHED]: 'Content published',
	[NotificationType.CONTENT_EXPIRING]: 'Content expiring',
	[NotificationType.WEBHOOK_SUBSCRIPTION_DISABLED]: 'Webhook subscription disabled',
	[NotificationType.SMS_TEST]: 'Test SMS',
	[NotificationType.SMS_SPEND_CAP_REACHED]: 'SMS spend cap reached',
	[NotificationType.OFFER_SENT]: 'Offer sent to customer',
	[NotificationType.LANDING_ACCESS_OTP]: 'Landing access code',
	[NotificationType.LANDING_ACCESS_MAGIC_LINK]: 'Landing access link',
	[NotificationType.FILE_EXCHANGE_FILE_MISSING]: 'Expected import file not received',
	[NotificationType.FILE_EXCHANGE_SECURITY_REJECTED]: 'Import file rejected by security scan',
	[NotificationType.FILE_EXCHANGE_INVALID_FILE]: 'Invalid import file',
	[NotificationType.FILE_EXCHANGE_COMPLETED_WITH_ERRORS]: 'Import completed with errors',
	[NotificationType.SAR_READY]: 'Subject access bundle ready',
}

/** One switch row. */
export interface NotificationToggleRow {
	notificationType: NotificationType
	enabled: boolean
	required: boolean
}

/**
 * Props for {@link NotificationTogglesPanel}.
 *
 * @property rows - One per notification type, in catalog order.
 * @property onToggle - Reports a switched row.
 * @property onSave - Saves the changed rows.
 * @property isDisabled - Read-only access.
 * @property isBusy - A save is in flight.
 */
export interface NotificationTogglesPanelProps {
	rows: NotificationToggleRow[]
	onToggle: (notificationType: NotificationType, enabled: boolean) => void
	onSave: () => void
	isDisabled?: boolean
	isBusy?: boolean
}

/** The tenant's notification switches for one channel (TO-382). A required row is locked on. Pure. */
export function NotificationTogglesPanel({
	rows,
	onToggle,
	onSave,
	isDisabled = false,
	isBusy = false,
}: NotificationTogglesPanelProps): ReactElement {
	return (
		<fieldset className="flex flex-col gap-3">
			<legend className="text-ds-lg">Notifications</legend>
			{rows.map((row) => (
				<div key={row.notificationType} className="flex items-center gap-3">
					<Switch
						isSelected={row.enabled}
						isDisabled={isDisabled || isBusy || row.required}
						onChange={(enabled) => onToggle(row.notificationType, enabled)}
					>
						{NOTIFICATION_TYPE_LABELS[row.notificationType]}
					</Switch>
					{row.required ? <span className="text-ds-sm text-ds-text-muted">Required</span> : null}
				</div>
			))}
			<div>
				<Button isDisabled={isDisabled || isBusy} onPress={onSave}>
					Save notifications
				</Button>
			</div>
		</fieldset>
	)
}
